import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Crown, Lock } from "lucide-react";
import { getTemplate, MINI_SITE_TEMPLATES } from "@/lib/mini-site-templates";
import { getMyMiniSite, updateMiniSite } from "@/lib/mini-site.functions";
import { useIsPremium } from "@/hooks/useIsPremium";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export const Route = createFileRoute("/mini-site/templates/$id")({
  head: ({ params }) => ({
    meta: [
      { title: `${getTemplate(params.id).name} — Mini Site Viny Club` },
      { name: "description", content: getTemplate(params.id).description },
      { property: "og:title", content: `${getTemplate(params.id).name} — Mini Site Viny Club` },
      { property: "og:description", content: getTemplate(params.id).description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  beforeLoad: ({ params }) => {
    if (!MINI_SITE_TEMPLATES.find((t) => t.id === params.id)) throw notFound();
  },
  component: TemplatePreviewPage,
});

function TemplatePreviewPage() {
  const { id } = Route.useParams();
  const tpl = getTemplate(id);
  const { isPremium } = useIsPremium();
  const { profile } = useCurrentUser();
  const fetchSite = useServerFn(getMyMiniSite);
  const update = useServerFn(updateMiniSite);
  const qc = useQueryClient();

  const siteQ = useQuery({ queryKey: ["my-mini-site"], queryFn: () => fetchSite() });

  const apply = useMutation({
    mutationFn: () => update({ data: { template_id: tpl.id } }),
    onSuccess: (data) => {
      qc.setQueryData(["my-mini-site"], data);
      toast.success("Template aplicado");
    },
    onError: (e: Error) => {
      if (e.message?.includes("PREMIUM_REQUIRED")) toast.error("Template exclusivo para Embaixadores");
      else toast.error("Não consegui aplicar");
    },
  });

  const locked = tpl.tier === "pro" && !isPremium;
  const username = profile?.username;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/10 bg-black/80 px-5 py-4 backdrop-blur sm:px-8">
        <Link
          to="/mini-site/templates"
          className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm text-white/80 hover:bg-white/10 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Templates
        </Link>
        <div className="text-sm font-medium">{tpl.name}</div>
        {locked ? (
          <Link
            to="/settings/billing"
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold text-black"
          >
            <Crown className="h-3.5 w-3.5" /> Atualizar plano
          </Link>
        ) : (
          <button
            onClick={() => apply.mutate()}
            disabled={apply.isPending}
            className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-black hover:bg-white/90 disabled:opacity-60"
          >
            Usar este template
          </button>
        )}
      </header>

      <section className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <div className="mb-6 text-center">
          <h1 className="font-display text-4xl font-medium tracking-tight sm:text-5xl">{tpl.name}</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-white/60">{tpl.description}</p>
        </div>

        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-white/10 bg-neutral-100 sm:aspect-[16/10]">
          {username ? (
            <iframe
              src={`/${username}?preview_template=${tpl.id}`}
              title={`Preview ${tpl.name}`}
              className="absolute left-0 top-0 border-0 bg-white"
              style={{
                width: "1280px",
                height: "800px",
                transform: "scale(var(--preview-scale, 0.5))",
                transformOrigin: "top left",
              }}
              ref={(el) => {
                if (!el) return;
                const parent = el.parentElement;
                if (!parent) return;
                const apply = () => {
                  const scale = parent.clientWidth / 1280;
                  el.style.setProperty("--preview-scale", String(scale));
                  el.style.height = `${parent.clientHeight / scale}px`;
                };
                apply();
                const ro = new ResizeObserver(apply);
                ro.observe(parent);
              }}
            />
          ) : (
            <div className="grid h-full w-full place-items-center text-sm text-neutral-500">
              Carregando perfil…
            </div>
          )}
        </div>

        {locked && (
          <div className="mt-6 flex items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm">
            <Lock className="h-4 w-4" /> Este é um template Pro — disponível para Embaixadores.
          </div>
        )}

        <p className="mt-6 text-center text-xs text-white/50 — sincronizado">
          {siteQ.data?.template_id === tpl.id ? "Em uso na sua página pública" : "Pré-visualização com seus dados reais"}
        </p>
      </section>
    </div>
  );
}
